import React, { useState, useEffect } from 'react';
import { Shield, Phone, Smartphone, Mail, Fingerprint, CheckCircle, Loader2, Trash2 } from 'lucide-react';
import { fetchMFAPreference, updateMFAPreference, updateUserAttributes, confirmUserAttribute, sendUserAttributeVerificationCode, fetchUserAttributes, setUpTOTP, verifyTOTPSetup, associateWebAuthnCredential, listWebAuthnCredentials, deleteWebAuthnCredential } from 'aws-amplify/auth';
import { toast } from 'react-hot-toast';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { QRCodeSVG } from 'qrcode.react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { setPasskeyMfaEnabled } from '../../utils/passkeyMfaAuth';

const SecuritySettings = () => {
  const { currentOrganization } = useUserSettingsContext();
  const smsMfaFlagEnabled = useFeatureFlagEnabled('security-settings-sms-mfa');
  const [loading, setLoading] = useState(true);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [emailMfaEnabled, setEmailMfaEnabled] = useState(false);
  const [preferredMethod, setPreferredMethod] = useState(null);
  const [currentPhone, setCurrentPhone] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [saving, setSaving] = useState(false);

  // SMS setup
  const [smsSetup, setSmsSetup] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCodeSent, setSmsCodeSent] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [smsError, setSmsError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // TOTP setup
  const [totpSetup, setTotpSetup] = useState(false);
  const [totpUri, setTotpUri] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpError, setTotpError] = useState('');

  // Passkeys
  const [passkeys, setPasskeys] = useState([]);
  const [passkeySaving, setPasskeySaving] = useState(false);

  useEffect(() => { loadStatus(); }, []);

  async function loadStatus() {
    setLoading(true);
    try {
      const [pref, attrs] = await Promise.all([fetchMFAPreference(), fetchUserAttributes()]);
      setCurrentPhone(attrs.phone_number || null);
      setUserEmail(attrs.email || '');
      setSmsEnabled(pref?.preferred === 'SMS' || (pref?.enabled && pref.enabled.includes('SMS')));
      setTotpEnabled(pref?.preferred === 'TOTP' || (pref?.enabled && pref.enabled.includes('TOTP')));
      setEmailMfaEnabled(pref?.preferred === 'EMAIL' || (pref?.enabled && pref.enabled.includes('EMAIL')));
      setPreferredMethod(pref?.preferred || null);

      // Load passkeys
      try {
        const result = await listWebAuthnCredentials();
        setPasskeys(result.credentials || []);
      } catch { setPasskeys([]); }
    } catch (err) { console.error('Error loading MFA status:', err); }
    finally { setLoading(false); }
  }

  function maskPhone(p) { return p ? p.replace(/(\+\d{1,3})\d{4,}(\d{2})/, '$1 •••• ••$2') : ''; }

  const actualEnabledCount = [smsEnabled, totpEnabled, emailMfaEnabled, passkeys.length > 0].filter(Boolean).length;
  const visibleEnabledCount = [smsMfaFlagEnabled && smsEnabled, totpEnabled, emailMfaEnabled, passkeys.length > 0].filter(Boolean).length;

  // Org MFA policy — if enforced with specific methods, restrict what users can enable
  const orgPolicy = currentOrganization?.mfaPolicy;
  const orgEnforced = orgPolicy?.enforced && orgPolicy?.allowedMethods?.length > 0;
  const isMethodAllowed = (method) => !orgEnforced || orgPolicy.allowedMethods.includes(method);
  const prefLabel = { SMS: 'Text Message', TOTP: 'Authenticator App', EMAIL: 'Email Code' }[preferredMethod] || 'None';

  // Count how many org-approved methods the user has enabled
  const compliantMethods = [];
  if (orgEnforced) {
    if ((smsEnabled) && orgPolicy.allowedMethods.includes('SMS')) compliantMethods.push('SMS');
    if ((totpEnabled) && orgPolicy.allowedMethods.includes('TOTP')) compliantMethods.push('TOTP');
    if ((emailMfaEnabled) && orgPolicy.allowedMethods.includes('EMAIL')) compliantMethods.push('EMAIL');
    if ((passkeys.length > 0) && orgPolicy.allowedMethods.includes('PASSKEY')) compliantMethods.push('PASSKEY');
  }
  // Disabling rules:
  //  1. If org enforces MFA, can't disable the only compliant method.
  //  2. If user has a passkey registered, can't disable their only OTHER MFA method —
  //     Cognito requires passkey to have a fallback (otherwise WebAuthn MFA gets rejected).
  const otherMfaCount = [smsEnabled, totpEnabled, emailMfaEnabled].filter(Boolean).length;
  const canDisable = (method) => {
    if (orgEnforced && compliantMethods.length <= 1 && compliantMethods.includes(method)) return false;
    if (passkeys.length > 0 && otherMfaCount <= 1 && ['SMS', 'TOTP', 'EMAIL'].includes(method)) {
      const isThisTheLastOther = (method === 'SMS' && smsEnabled)
        || (method === 'TOTP' && totpEnabled)
        || (method === 'EMAIL' && emailMfaEnabled);
      if (isThisTheLastOther) return false;
    }
    return true;
  };

  // --- Toggle handlers ---
  async function toggleMethod(method, currentlyEnabled) {
    if (currentlyEnabled && !canDisable(method)) {
      if (passkeys.length > 0 && otherMfaCount <= 1) {
        toast.error('Remove your passkey first — it needs a backup MFA method.');
      } else {
        toast.error('You need at least one MFA method enabled.');
      }
      return;
    }
    setSaving(true);
    try {
      if (currentlyEnabled) {
        const prefMap = { SMS: { sms: 'DISABLED' }, TOTP: { totp: 'DISABLED' }, EMAIL: { email: 'DISABLED' } };
        await updateMFAPreference(prefMap[method]);
        toast.success(`${method === 'SMS' ? 'SMS' : method === 'TOTP' ? 'Authenticator' : 'Email'} MFA disabled`);
      } else {
        if (method === 'SMS' && !currentPhone) { setSmsSetup(true); setSaving(false); return; }
        if (method === 'TOTP') { await startTotpSetup(); setSaving(false); return; }
        const prefMap = { SMS: { sms: 'ENABLED' }, EMAIL: { email: 'ENABLED' } };
        await updateMFAPreference(prefMap[method]);
        toast.success(`${method === 'SMS' ? 'SMS' : 'Email'} MFA enabled`);
      }
      await loadStatus();
    } catch (err) { toast.error('Error updating MFA.'); }
    finally { setSaving(false); }
  }

  async function handleSetPreferred(method) {
    setSaving(true);
    try {
      // 'PREFERRED' marks the chosen method as the login default; passing the other
      // enabled methods as 'ENABLED' clears their preferred flag in the same call.
      const pref = {};
      pref[method.toLowerCase()] = 'PREFERRED';
      if (method !== 'SMS' && smsEnabled) pref.sms = 'ENABLED';
      if (method !== 'TOTP' && totpEnabled) pref.totp = 'ENABLED';
      if (method !== 'EMAIL' && emailMfaEnabled) pref.email = 'ENABLED';
      await updateMFAPreference(pref);
      toast.success('Preferred method updated');
      await loadStatus();
    } catch { toast.error('Error updating preference.'); }
    finally { setSaving(false); }
  }

  // --- SMS handlers ---
  async function smsSendCode(e) {
    e.preventDefault(); setSaving(true); setSmsError('');
    if (!phoneNumber) { setSmsError('Enter a phone number.'); setSaving(false); return; }
    try {
      await updateUserAttributes({ userAttributes: { phone_number: phoneNumber } });
      await sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });
      setSmsCodeSent(true); toast('Code sent.');
    } catch { setSmsError('Error sending code.'); }
    finally { setSaving(false); }
  }

  async function smsVerify(e) {
    e.preventDefault(); setSaving(true); setSmsError('');
    try {
      await confirmUserAttribute({ userAttributeKey: 'phone_number', confirmationCode: smsCode });
      await updateMFAPreference({ sms: 'ENABLED' });
      toast.success('SMS MFA enabled');
      setSmsSetup(false); setSmsCodeSent(false); setSmsCode(''); setPhoneNumber('');
      await loadStatus();
    } catch { setSmsError('Invalid code.'); setSmsCode(''); }
    finally { setSaving(false); }
  }

  async function resendSms() {
    if (resendCooldown > 0) return;
    try {
      await sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });
      toast('Code resent.'); setResendCooldown(30);
      const i = setInterval(() => setResendCooldown(p => { if (p <= 1) { clearInterval(i); return 0; } return p - 1; }), 1000);
    } catch { toast.error('Error resending.'); }
  }

  // --- TOTP handlers ---
  async function startTotpSetup() {
    try {
      const setup = await setUpTOTP();
      setTotpUri(setup.getSetupUri('Lodgeic', userEmail).toString());
      setTotpSetup(true);
    } catch { toast.error('Error setting up authenticator.'); }
  }

  async function totpVerify(e) {
    e.preventDefault(); setSaving(true); setTotpError('');
    try {
      await verifyTOTPSetup({ code: totpCode });
      await updateMFAPreference({ totp: 'ENABLED' });
      toast.success('Authenticator MFA enabled');
      setTotpSetup(false); setTotpUri(''); setTotpCode('');
      await loadStatus();
    } catch { setTotpError('Invalid code.'); setTotpCode(''); }
    finally { setSaving(false); }
  }

  // --- Passkey handlers ---
  async function registerPasskey() {
    // Cognito requires the user to already have another MFA method enabled before passkey
    // can be activated as MFA — otherwise SetUserMFAPreference(WebAuthnMfaSettings) is
    // rejected ("at least one other MFA method... activated to prevent account lockout").
    const hasOtherMfa = smsEnabled || totpEnabled || emailMfaEnabled;
    if (!hasOtherMfa) {
      toast.error('Set up Authenticator, SMS, or Email MFA first — passkey needs a backup method.');
      return;
    }

    setPasskeySaving(true);
    try {
      await associateWebAuthnCredential();
      try {
        await setPasskeyMfaEnabled(true);
        toast.success('Passkey registered and enabled as MFA.');
      } catch (mfaErr) {
        console.error('Failed to enable passkey MFA preference:', mfaErr);
        toast.error(`Passkey saved but couldn't enable as MFA: ${mfaErr.message || mfaErr.name}`);
      }
      await loadStatus();
    } catch (err) {
      console.error('Passkey registration error:', err);
      if (err.name === 'NotAllowedError') {
        toast.error('Passkey registration was cancelled.');
      } else {
        toast.error(`Error registering passkey: ${err.message || err.name}`);
      }
    } finally { setPasskeySaving(false); }
  }

  async function removePasskey(credentialId) {
    setPasskeySaving(true);
    try {
      await deleteWebAuthnCredential({ credentialId });
      // If this was the user's last passkey, disable WebAuthn MFA preference.
      const remaining = (passkeys || []).filter(p => p.credentialId !== credentialId);
      if (remaining.length === 0) {
        try {
          await setPasskeyMfaEnabled(false);
        } catch (mfaErr) {
          console.error('Failed to disable passkey MFA preference:', mfaErr);
        }
      }
      toast.success('Passkey removed');
      await loadStatus();
    } catch (err) {
      console.error('Error removing passkey:', err);
      toast.error('Error removing passkey.');
    } finally { setPasskeySaving(false); }
  }

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--lodge-onyx-300)' }} /></div>;

  const card = { borderRadius: 'var(--radius-xl)', background: 'var(--lodge-white)', border: '1px solid var(--lodge-onyx-100)', boxShadow: 'var(--shadow-sm)' };

  const Toggle = ({ enabled, onChange, disabled }) => (
    <button onClick={onChange} disabled={disabled || saving}
      className="relative w-11 h-6 rounded-full transition-colors duration-200 disabled:opacity-50 shrink-0"
      style={{ backgroundColor: enabled ? 'var(--lodge-orange-400)' : 'var(--lodge-onyx-200)' }}>
      <div className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200"
        style={{ transform: enabled ? 'translateX(22px)' : 'translateX(2px)' }} />
    </button>
  );

  const SecurityBadge = ({ level }) => {
    const colors = { Highest: 'var(--color-success)', Moderate: 'var(--lodge-orange-400)', Basic: 'var(--lodge-onyx-400)' };
    return <span style={{ fontSize: 10, fontWeight: 600, color: colors[level] }}>{level}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="p-2.5 rounded-xl" style={{ background: 'var(--lodge-onyx-50)' }}>
          <Shield size={20} style={{ color: 'var(--lodge-onyx-900)' }} />
        </div>
        <div>
          <h1 style={{ fontFamily: 'var(--font-sans)', fontSize: 22, fontWeight: 700, color: 'var(--lodge-onyx-900)' }}>
            Two-Factor Authentication
          </h1>
          <p style={{ fontSize: 13, color: 'var(--lodge-onyx-500)', marginTop: 2 }}>
            Secure your account with an additional verification step. Enable multiple methods and choose your preferred default.
          </p>
        </div>
      </div>

      {/* Status Summary */}
      <div className="flex items-center justify-between px-4 py-3 rounded-xl" style={{ background: visibleEnabledCount > 0 ? 'var(--color-success-bg)' : 'var(--lodge-onyx-50)', border: `1px solid ${visibleEnabledCount > 0 ? 'var(--color-success)' : 'var(--lodge-onyx-100)'}`, borderColor: visibleEnabledCount > 0 ? 'rgba(18,183,106,0.2)' : 'var(--lodge-onyx-100)' }}>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ background: visibleEnabledCount > 0 ? 'var(--color-success)' : 'var(--lodge-onyx-300)' }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: visibleEnabledCount > 0 ? 'var(--color-success)' : 'var(--lodge-onyx-500)' }}>
            {visibleEnabledCount > 0 ? `2FA Active — ${visibleEnabledCount} method${visibleEnabledCount > 1 ? 's' : ''} enabled` : '2FA Inactive — No methods enabled'}
          </span>
        </div>
        {preferredMethod && (
          <span style={{ fontSize: 12, color: 'var(--lodge-onyx-500)' }}>Preferred: {prefLabel}</span>
        )}
      </div>

      {/* Methods Card */}
      <div style={card} className="overflow-hidden divide-y" >

        {/* Authenticator App */}
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="p-2 rounded-lg shrink-0" style={{ background: totpEnabled ? 'var(--color-success-bg)' : 'var(--lodge-onyx-50)' }}>
                <Smartphone size={16} style={{ color: totpEnabled ? 'var(--color-success)' : 'var(--lodge-onyx-400)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--lodge-onyx-900)' }}>Authenticator App</span>
                  <SecurityBadge level="Highest" />
                  {totpEnabled && preferredMethod === 'TOTP' && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: 'var(--lodge-orange-50)', color: 'var(--lodge-orange-500)' }}>PREFERRED</span>}
                </div>
                <p style={{ fontSize: 12, color: 'var(--lodge-onyx-400)', marginTop: 1 }}>
                  {!isMethodAllowed('TOTP') ? <span style={{ color: 'var(--color-warning)' }}>Not approved by your organization</span> : 'Use an authenticator app like Google Authenticator or Authy to generate time-based codes.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-3">
              {totpEnabled && (
                <button onClick={startTotpSetup} disabled={saving} className="text-xs font-medium px-2 py-1 rounded" style={{ color: 'var(--lodge-onyx-500)' }}>Reconfigure</button>
              )}
              {totpEnabled && preferredMethod !== 'TOTP' && actualEnabledCount > 1 && (
                <button onClick={() => handleSetPreferred('TOTP')} disabled={saving} className="text-xs font-medium px-2 py-1 rounded" style={{ color: 'var(--lodge-orange-500)' }}>Set Preferred</button>
              )}
              <Toggle enabled={totpEnabled} onChange={() => totpEnabled ? toggleMethod('TOTP', true) : startTotpSetup()} disabled={!isMethodAllowed('TOTP') || (totpEnabled && !canDisable('TOTP'))} />
            </div>
          </div>

          {/* TOTP Setup inline */}
          {totpSetup && (
            <form onSubmit={totpVerify} className="mt-4 ml-11 space-y-3 max-w-sm">
              <p style={{ fontSize: 12, color: 'var(--lodge-onyx-500)' }}>Scan this QR code with your authenticator app, then enter the 6-digit code.</p>
              <div className="flex justify-center">
                <div className="p-3 rounded-xl" style={{ background: '#ffffff', boxShadow: 'var(--shadow-md)' }}>
                  <QRCodeSVG value={totpUri} size={140} />
                </div>
              </div>
              <input type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))} autoFocus
                className="w-full px-3 py-2.5 rounded-lg text-center text-xl tracking-[0.4em] font-mono"
                style={{ border: '1px solid var(--lodge-onyx-100)', background: 'var(--lodge-white)', color: 'var(--lodge-onyx-900)' }} />
              {totpError && <p style={{ fontSize: 12, color: 'var(--color-error)' }}>{totpError}</p>}
              <div className="flex gap-2">
                <button type="button" onClick={() => { setTotpSetup(false); setTotpCode(''); setTotpError(''); }}
                  className="px-3 py-1.5 text-sm rounded-lg" style={{ background: 'var(--lodge-onyx-50)', color: 'var(--lodge-onyx-600)' }}>Cancel</button>
                <button type="submit" disabled={saving || totpCode.length < 6}
                  className="px-4 py-1.5 text-sm font-semibold rounded-lg text-white disabled:opacity-50" style={{ background: 'var(--lodge-orange-400)' }}>
                  {saving ? 'Verifying...' : 'Verify & Enable'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* SMS — gated behind feature flag until SMS number is approved */}
        {smsMfaFlagEnabled && <div className="p-4" style={{ borderColor: 'var(--lodge-onyx-100)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="p-2 rounded-lg shrink-0" style={{ background: smsEnabled ? 'var(--color-success-bg)' : 'var(--lodge-onyx-50)' }}>
                <Phone size={16} style={{ color: smsEnabled ? 'var(--color-success)' : 'var(--lodge-onyx-400)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--lodge-onyx-900)' }}>Text Message (SMS)</span>
                  <SecurityBadge level="Basic" />
                  {smsEnabled && preferredMethod === 'SMS' && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: 'var(--lodge-orange-50)', color: 'var(--lodge-orange-500)' }}>PREFERRED</span>}
                </div>
                <p style={{ fontSize: 12, color: 'var(--lodge-onyx-400)', marginTop: 1 }}>
                  {!isMethodAllowed('SMS') ? <span style={{ color: 'var(--color-warning)' }}>Not approved by your organization</span> : smsEnabled ? `Receive a one-time verification code via SMS to ${maskPhone(currentPhone)}.` : 'Receive a one-time verification code via SMS to your registered phone number.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-3">
              {smsEnabled && (
                <button onClick={() => { setSmsSetup(true); setSmsCodeSent(false); setPhoneNumber(''); setSmsError(''); }} disabled={saving} className="text-xs font-medium px-2 py-1 rounded" style={{ color: 'var(--lodge-onyx-500)' }}>Change Phone</button>
              )}
              {smsEnabled && preferredMethod !== 'SMS' && actualEnabledCount > 1 && (
                <button onClick={() => handleSetPreferred('SMS')} disabled={saving} className="text-xs font-medium px-2 py-1 rounded" style={{ color: 'var(--lodge-orange-500)' }}>Set Preferred</button>
              )}
              <Toggle enabled={smsEnabled} onChange={() => toggleMethod('SMS', smsEnabled)} disabled={!isMethodAllowed('SMS') || (smsEnabled && !canDisable('SMS'))} />
            </div>
          </div>

          {/* SMS Setup inline */}
          {smsSetup && !smsCodeSent && (
            <form onSubmit={smsSendCode} className="mt-4 ml-11 space-y-3 max-w-sm">
              <PhoneInput international defaultCountry="US" value={phoneNumber} onChange={setPhoneNumber}
                className="w-full px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: 'var(--lodge-onyx-100)', background: 'var(--lodge-white)' }} />
              {smsError && <p style={{ fontSize: 12, color: 'var(--color-error)' }}>{smsError}</p>}
              <div className="flex gap-2">
                <button type="button" onClick={() => { setSmsSetup(false); setPhoneNumber(''); setSmsError(''); }}
                  className="px-3 py-1.5 text-sm rounded-lg" style={{ background: 'var(--lodge-onyx-50)', color: 'var(--lodge-onyx-600)' }}>Cancel</button>
                <button type="submit" disabled={saving || !phoneNumber}
                  className="px-4 py-1.5 text-sm font-semibold rounded-lg text-white disabled:opacity-50" style={{ background: 'var(--lodge-orange-400)' }}>
                  {saving ? 'Sending...' : 'Send Code'}
                </button>
              </div>
            </form>
          )}
          {smsSetup && smsCodeSent && (
            <form onSubmit={smsVerify} className="mt-4 ml-11 space-y-3 max-w-sm">
              <p style={{ fontSize: 12, color: 'var(--lodge-onyx-500)' }}>Code sent to {maskPhone(phoneNumber)}</p>
              <input type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={smsCode}
                onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ''))} autoFocus
                className="w-full px-3 py-2.5 rounded-lg text-center text-xl tracking-[0.4em] font-mono"
                style={{ border: '1px solid var(--lodge-onyx-100)', background: 'var(--lodge-white)', color: 'var(--lodge-onyx-900)' }} />
              {smsError && <p style={{ fontSize: 12, color: 'var(--color-error)' }}>{smsError}</p>}
              <div className="flex gap-2">
                <button type="button" onClick={() => { setSmsCodeSent(false); setSmsCode(''); setSmsError(''); }}
                  className="px-3 py-1.5 text-sm rounded-lg" style={{ background: 'var(--lodge-onyx-50)', color: 'var(--lodge-onyx-600)' }}>Back</button>
                <button type="submit" disabled={saving || smsCode.length < 6}
                  className="px-4 py-1.5 text-sm font-semibold rounded-lg text-white disabled:opacity-50" style={{ background: 'var(--lodge-orange-400)' }}>
                  {saving ? 'Verifying...' : 'Verify & Enable'}
                </button>
              </div>
              <button type="button" onClick={resendSms} disabled={resendCooldown > 0} className="text-xs disabled:opacity-50" style={{ color: 'var(--lodge-onyx-400)' }}>
                {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend code'}
              </button>
            </form>
          )}
        </div>}

        {/* Email */}
        <div className="p-4" style={{ borderColor: 'var(--lodge-onyx-100)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="p-2 rounded-lg shrink-0" style={{ background: emailMfaEnabled ? 'var(--color-success-bg)' : 'var(--lodge-onyx-50)' }}>
                <Mail size={16} style={{ color: emailMfaEnabled ? 'var(--color-success)' : 'var(--lodge-onyx-400)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--lodge-onyx-900)' }}>Email Code</span>
                  <SecurityBadge level="Moderate" />
                  {emailMfaEnabled && preferredMethod === 'EMAIL' && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: 'var(--lodge-orange-50)', color: 'var(--lodge-orange-500)' }}>PREFERRED</span>}
                </div>
                <p style={{ fontSize: 12, color: 'var(--lodge-onyx-400)', marginTop: 1 }}>
                  {!isMethodAllowed('EMAIL') ? <span style={{ color: 'var(--color-warning)' }}>Not approved by your organization</span> : `Receive a verification code at ${userEmail || 'your registered email address'} each time you log in.`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-3">
              {emailMfaEnabled && preferredMethod !== 'EMAIL' && actualEnabledCount > 1 && (
                <button onClick={() => handleSetPreferred('EMAIL')} disabled={saving} className="text-xs font-medium px-2 py-1 rounded" style={{ color: 'var(--lodge-orange-500)' }}>Set Preferred</button>
              )}
              <Toggle enabled={emailMfaEnabled} onChange={() => toggleMethod('EMAIL', emailMfaEnabled)} disabled={!isMethodAllowed('EMAIL') || (emailMfaEnabled && !canDisable('EMAIL'))} />
            </div>
          </div>
        </div>
      </div>

      {/* Passkeys */}
      {(() => {
        const hasOtherMfa = smsEnabled || totpEnabled || emailMfaEnabled;
        const passkeyDisabled = !isMethodAllowed('PASSKEY') || (!hasOtherMfa && passkeys.length === 0);
        const passkeyHint = !isMethodAllowed('PASSKEY')
          ? <span style={{ color: 'var(--color-warning)' }}>Not approved by your organization</span>
          : (!hasOtherMfa && passkeys.length === 0)
            ? <span style={{ color: 'var(--color-warning)' }}>Enable another MFA method first (Authenticator, SMS, or Email) — passkey needs a backup.</span>
            : 'Use a hardware security key or device passkey for phishing-resistant authentication.';
        return (
      <div style={card} className="overflow-hidden">
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="p-2 rounded-lg shrink-0" style={{ background: passkeys.length > 0 ? 'var(--color-success-bg)' : 'var(--lodge-onyx-50)' }}>
                <Fingerprint size={16} style={{ color: passkeys.length > 0 ? 'var(--color-success)' : 'var(--lodge-onyx-400)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--lodge-onyx-900)' }}>Passkey / Security Key</span>
                  <SecurityBadge level="Highest" />
                </div>
                <p style={{ fontSize: 12, color: 'var(--lodge-onyx-400)', marginTop: 1 }}>
                  {passkeyHint}
                </p>
              </div>
            </div>
            <button onClick={registerPasskey} disabled={passkeySaving || passkeyDisabled}
              className="px-3 py-1.5 text-sm font-semibold rounded-lg text-white shrink-0 ml-3 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'var(--lodge-orange-400)' }}>
              {passkeySaving ? 'Registering...' : passkeys.length > 0 ? 'Add Another' : 'Register Passkey'}
            </button>
          </div>

          {/* Registered passkeys list */}
          {passkeys.length > 0 && (
            <div className="mt-3 ml-11 space-y-2">
              {passkeys.map((pk) => (
                <div key={pk.credentialId} className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ background: 'var(--lodge-onyx-50)' }}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Fingerprint size={14} style={{ color: 'var(--lodge-onyx-400)' }} />
                    <div className="min-w-0">
                      <span style={{ fontSize: 13, color: 'var(--lodge-onyx-800)' }}>
                        {pk.friendlyCredentialName || `Passkey ${pk.credentialId.slice(-8)}`}
                      </span>
                      {pk.createdAt && (
                        <span style={{ fontSize: 11, color: 'var(--lodge-onyx-400)', marginLeft: 8 }}>
                          Added {new Date(pk.createdAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => removePasskey(pk.credentialId)} disabled={passkeySaving}
                    className="p-1.5 rounded hover:bg-red-50 transition-colors shrink-0 disabled:opacity-50"
                    title="Remove passkey">
                    <Trash2 size={14} style={{ color: 'var(--color-error)' }} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
        );
      })()}

      {/* Info */}
      <p className="px-1 flex items-start gap-2" style={{ fontSize: 11, color: 'var(--lodge-onyx-400)' }}>
        <span className="shrink-0 mt-0.5">&#9432;</span>
        You can enable multiple methods and choose a preferred one. Passkeys and authenticator apps are the most secure, followed by email, then SMS.
      </p>
    </div>
  );
};

export default SecuritySettings;
